import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "src/components/Avatar";
import useAuth from "src/hooks/selectors/useAuth";
import useBlur from "src/hooks/useBlur";
import useLogout from "src/hooks/useLogout";
import Link from "src/components/Link";

const UserProfileButton = () => {
	const { t } = useTranslation("layout", {
		keyPrefix: "nav-items",
	});
	const [visible, setVisible] = useState(false);
	const auth = useAuth();
	const { loading: loggingOut, handleLogout } = useLogout();
	const type = auth.type;

	const ref = useRef() as any;
	useBlur(ref, () => setVisible(false));

	return (
		<div {...{ ref }} className="relative">
			<div
				onClick={() => setVisible(!visible)}
				className="flex items-center gap-2 h-12 cursor-pointer dark:text-white"
			>
				<Avatar.User
					user={auth}
					avatar={{
						size: "button",
					}}
				/>
				<i
					className={`fas fa-caret-${visible ? "up" : "down"} ml-2`}
				></i>
			</div>
			<AnimatePresence>
				{visible && (
					<motion.div
						className={`bg-dark-accent rounded-md shadow absolute top-12 z-[99] right-0 flex flex-col min-w-max`}
						initial={{ opacity: 0, y: -20 }}
						exit={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
					>
						<UserProfileButtonItem
							to="/profile/show"
							icon="fal fa-user"
							label={
								type === "student"
									? t("edit-profile")
									: t("my-profile")
							}
						/>
						<UserProfileButtonItem
							to="/settings/notifications"
							icon="fal fa-cogs"
							label={t("settings")}
						/>
						<UserProfileButtonItem
							to="/reviews/about-me"
							icon="fal fa-star"
							label={t("reviews")}
						/>
						<UserProfileButtonItem
							to="/envelopes"
							icon="fal fa-file-signature"
							label={t("agreements")}
						/>
						<UserProfileButtonItem
							icon="fal fa-sign-out-alt"
							label={t("signout")}
							onClick={handleLogout}
							loading={loggingOut}
						/>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

interface UserProfileButtonItemProps {
	to?: string;
	onClick?: () => void;
	icon: string;
	label: string;
	loading?: boolean;
}

const UserProfileButtonItem = ({
	to,
	onClick,
	icon,
	label,
	loading,
}: UserProfileButtonItemProps) => {
	const auth = useAuth();

	if (onClick) {
		return (
			<div
				className={`p-4 flex items-center cursor-pointer gap-2 transition-all hover:opacity-80 ${
					auth.type === "company" ? "text-white" : ""
				}`}
				{...{ onClick }}
			>
				{loading ? (
					<span className="base-loader"></span>
				) : (
					<i className={`${icon}`}></i>
				)}
				<span>{label}</span>
			</div>
		);
	}

	return (
		<Link
			className={`p-4 flex items-center gap-2 transition-all hover:opacity-80 ${
				auth.type === "company" ? "text-white" : ""
			}`}
			activeClassName="underline"
			to={to || "/"}
		>
			{loading ? (
				<span className="base-loader"></span>
			) : (
				<i className={`${icon}`}></i>
			)}
			<span>{label}</span>
		</Link>
	);
};

export default UserProfileButton;
